/**
 * GlobalOfflineBanner
 * ────────────────────
 * Slim top banner shown app-wide when:
 *   • Device is offline
 *   • There are pending mutations waiting to sync
 *   • A sync is in progress
 *
 * Rendered inside Routes.jsx so it appears on every screen automatically.
 */

import React from 'react';
import Icon from '../AppIcon';
import { useOfflineQueue } from '../../contexts/OfflineQueueContext';

const GlobalOfflineBanner = () => {
  const { isOnline, pendingCount, syncing, lastSyncAt, flush } = useOfflineQueue();

  // Nothing to show
  if (isOnline && pendingCount === 0 && !syncing) return null;

  const formatTime = (date) => {
    if (!date) return '';
    return date?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // ── Offline ──────────────────────────────────────────────────────────────────
  if (!isOnline) {
    return (
      <div className="w-full flex items-center gap-2 px-4 py-2 bg-yellow-50 border-b border-yellow-200 z-50">
        <Icon name="WifiOff" size={14} color="#d97706" />
        <span className="text-xs font-semibold text-yellow-800">
          Working offline
        </span>
        {pendingCount > 0 && (
          <span className="text-xs text-yellow-700">
            — {pendingCount} change{pendingCount !== 1 ? 's' : ''} will sync when reconnected
          </span>
        )}
      </div>
    );
  }

  // ── Syncing ──────────────────────────────────────────────────────────────────
  if (syncing) {
    return (
      <div className="w-full flex items-center gap-2 px-4 py-2 bg-blue-50 border-b border-blue-200 z-50">
        <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
        <span className="text-xs font-semibold text-blue-800">
          Syncing {pendingCount} pending change{pendingCount !== 1 ? 's' : ''}…
        </span>
      </div>
    );
  }

  // ── Online with pending mutations ─────────────────────────────────────────────
  if (isOnline && pendingCount > 0) {
    return (
      <div className="w-full flex items-center gap-2 px-4 py-2 bg-orange-50 border-b border-orange-200 z-50">
        <Icon name="RefreshCw" size={14} color="#ea580c" />
        <span className="text-xs font-semibold text-orange-800">
          {pendingCount} change{pendingCount !== 1 ? 's' : ''} pending sync
        </span>
        {lastSyncAt && (
          <span className="text-xs text-orange-600 ml-1">
            · Last synced {formatTime(lastSyncAt)}
          </span>
        )}
        <button
          onClick={flush}
          className="ml-auto text-xs font-semibold bg-orange-200 text-orange-800 px-2.5 py-1 rounded-md hover:bg-orange-300 transition-colors"
        >
          Sync Now
        </button>
      </div>
    );
  }

  return null;
};

export default GlobalOfflineBanner;
